import { type ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { decorativeIcon, TriangleAlert } from '../../lib/icons';

export interface EmptyStateProps {
  /**
   * A SENTENCE naming the state in plain French — "Aucune exception dans cette
   * période." Not a noun, not "Aucun résultat", and never a heading that could
   * be mistaken for a section title.
   */
  readonly title: ReactNode;
  /** One line explaining what would put something here. */
  readonly description?: ReactNode;
  /** The single action that makes sense — or nothing. */
  readonly action?: ReactNode;
  /** `2` inside a page, `3` inside a panel. */
  readonly headingLevel?: 2 | 3 | 4;
  readonly className?: string;
}

/**
 * A designed empty state.
 *
 * No illustration, no mascot, no "Oops!". A live view legitimately shows zero
 * rows for most of the working day — this is the most-seen state of several
 * screens, not an edge case, and an undesigned empty grid there reads as a
 * broken page.
 */
export function EmptyState({
  title,
  description,
  action,
  headingLevel = 3,
  className,
}: EmptyStateProps) {
  const Heading = `h${headingLevel}` as const;

  return (
    <div
      className={cn(
        'flex flex-col items-start gap-2 px-4 py-10 text-start',
        'sm:items-center sm:text-center',
        className,
      )}
    >
      <Heading className="text-title-2 text-ink">{title}</Heading>
      {description !== undefined ? (
        <p className="max-w-prose text-support text-ink-muted">{description}</p>
      ) : null}
      {action !== undefined ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export interface ErrorStateProps extends EmptyStateProps {
  /**
   * A `SupportLine`. Admin surfaces only — a worker never sees an error code,
   * an enum name or an HTTP status.
   */
  readonly support?: ReactNode;
}

/**
 * A load that failed, as opposed to a load that returned nothing.
 *
 * The distinction matters: "Aucune exception" and "Impossible de charger les
 * exceptions" call for completely different actions, and collapsing them is how
 * a supervisor approves a period believing it was clean.
 */
export function ErrorState({
  title,
  description,
  action,
  support,
  headingLevel = 3,
  className,
}: ErrorStateProps) {
  const Heading = `h${headingLevel}` as const;

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-start gap-2 px-4 py-10 text-start',
        'sm:items-center sm:text-center',
        className,
      )}
    >
      <TriangleAlert
        {...decorativeIcon('lg')}
        className="shrink-0 text-status-danger-ink"
      />
      <Heading className="text-title-2 text-ink">{title}</Heading>
      {description !== undefined ? (
        <p className="max-w-prose text-support text-ink-muted">{description}</p>
      ) : null}
      {action !== undefined ? <div className="mt-2">{action}</div> : null}
      {support !== undefined ? <div className="mt-3">{support}</div> : null}
    </div>
  );
}
